import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Stack, MenuItem, TextField, CircularProgress, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { absenceApi, employeeApi, Absence } from '../../services/employeeService';
import {
  PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone, useConfirm,
} from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDate } from '../../utils/format';

const TYPE_LABEL: Record<string, string> = {
  SICK_LEAVE: 'L4', VACATION: 'Urlop', EXCUSED: 'Nieobecność usprawiedliwiona', UNEXCUSED: 'Nieobecność nieusprawiedliwiona', OTHER: 'Inna nieobecność',
};
const TYPE_OPTION_LABEL: Record<string, string> = { ...TYPE_LABEL, SICK_LEAVE: 'L4 (zwolnienie lekarskie)' };

const ABSENCE_STATUS: Record<string, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Oczekuje', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzona', tone: 'success' },
  REJECTED: { label: 'Odrzucona', tone: 'danger' },
  NEEDS_INFO: { label: 'Wymaga uzupełnienia', tone: 'info' },
};

const ERR_STAFF = 'Wybierz pracownika.';
const ERR_DATES = 'Uzupełnij zakres dat.';

const dayCount = (start: string, end: string) => {
  const a = new Date(start.slice(0, 10));
  const b = new Date(end.slice(0, 10));
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000) + 1);
};

interface StaffRow { id: string; firstName: string; lastName: string; }

export const AbsencesAdminPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const confirm = useConfirm();
  const [rows, setRows] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // Dodawanie nieobecności pracownikowi przez właściciela/managera
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [addForm, setAddForm] = useState({ staffId: '', type: 'SICK_LEAVE', startDate: '', endDate: '', note: '' });

  useEffect(() => {
    if (!activeSalonId) { setStaffList([]); return; }
    employeeApi.list(activeSalonId).then((r: StaffRow[]) => setStaffList(r)).catch(() => setStaffList([]));
  }, [activeSalonId]);

  const openAdd = () => {
    const today = new Date().toISOString().slice(0, 10);
    setAddForm((f) => ({ ...f, staffId: f.staffId || staffList[0]?.id || '', startDate: today, endDate: today, note: '' }));
    setAddError('');
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.staffId) { setAddError(ERR_STAFF); return; }
    if (!addForm.startDate || !addForm.endDate) { setAddError(ERR_DATES); return; }
    setAddSaving(true);
    setAddError('');
    try {
      await absenceApi.create({
        staffId: addForm.staffId,
        type: addForm.type,
        startDate: addForm.startDate,
        endDate: addForm.endDate,
        ...(addForm.note.trim() ? { note: addForm.note.trim() } : {}),
      });
      setAddOpen(false);
      setToast('Nieobecność dodana i zatwierdzona');
      load();
    } catch (e) {
      const d = (e as { response?: { data?: { error?: string; errors?: { msg?: string }[] } } }).response?.data;
      setAddError(d?.error || d?.errors?.[0]?.msg || 'Nie udało się dodać nieobecności. Sprawdź daty i spróbuj ponownie.');
    } finally {
      setAddSaving(false);
    }
  };

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    try { setRows(await absenceApi.list({ salonId: activeSalonId, ...(statusFilter ? { status: statusFilter } : {}) })); }
    finally { setLoading(false); }
  }, [activeSalonId, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const review = async (id: string, status: string) => {
    await absenceApi.review(id, status);
    setToast('Zaktualizowano'); load();
  };

  const reject = async (r: Absence) => {
    const ok = await confirm({
      title: 'Odrzucić zgłoszenie nieobecności?',
      details: (
        <>
          <strong>{r.staff?.firstName} {r.staff?.lastName}</strong> · {TYPE_LABEL[r.type]}<br />
          {formatDate(r.startDate)} – {formatDate(r.endDate)}
        </>
      ),
      description: 'Pracownik zobaczy w swoim panelu status „Odrzucona”.',
      confirmLabel: 'Odrzuć zgłoszenie',
      tone: 'danger',
    });
    if (ok) review(r.id, 'REJECTED');
  };

  const pending = rows.filter((r) => r.status === 'PENDING').length;
  const pendingInList = statusFilter === '' || statusFilter === 'PENDING';

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Nieobecności i L4"
        description={!loading && pendingInList && pending > 0
          ? `${countLabel(pending, 'zgłoszenie czeka', 'zgłoszenia czekają', 'zgłoszeń czeka')} na zatwierdzenie`
          : 'Urlopy, zwolnienia lekarskie i inne nieobecności zespołu.'}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Dodaj nieobecność
          </Button>
        )}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ 'aria-label': 'Status' }}
            sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            <MenuItem value="">Wszystkie statusy</MenuItem>
            <MenuItem value="PENDING">Oczekujące</MenuItem>
            <MenuItem value="APPROVED">Zatwierdzone</MenuItem>
            <MenuItem value="REJECTED">Odrzucone</MenuItem>
          </TextField>
        </Stack>
      </PageHeader>

      {loading ? (
        <LoadingState variant="table" rows={5} />
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<EventAvailableOutlinedIcon />}
            title={statusFilter ? 'Brak zgłoszeń o tym statusie' : 'Brak zgłoszonych nieobecności'}
            description={statusFilter
              ? 'Zmień filtr, aby zobaczyć pozostałe zgłoszenia.'
              : 'Pracownicy zgłaszają urlop i L4 w swoim panelu. Nieobecność możesz też dodać sam — będzie od razu zatwierdzona.'}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Zgłoszenia" subtitle={countLabel(rows.length, 'nieobecność', 'nieobecności', 'nieobecności')} padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pracownik</TableCell>
                  <TableCell>Rodzaj</TableCell>
                  <TableCell>Termin</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" aria-label="Akcje" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const st = ABSENCE_STATUS[r.status] || { label: r.status, tone: 'neutral' as PillTone };
                  const days = dayCount(r.startDate, r.endDate);
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <PersonAvatar firstName={r.staff?.firstName} lastName={r.staff?.lastName} seed={r.staff?.id} size={32} />
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{r.staff?.firstName} {r.staff?.lastName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography sx={{ fontSize: 'inherit', fontWeight: 600 }}>{TYPE_LABEL[r.type] || 'Inna nieobecność'}</Typography>
                        {r.note && <Typography variant="caption" component="div" sx={{ maxWidth: 260 }}>{r.note}</Typography>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                          {days === 1 ? formatDate(r.startDate) : `${formatDate(r.startDate)} – ${formatDate(r.endDate)}`}
                        </Typography>
                        <Typography variant="caption" component="div">{countLabel(days, 'dzień', 'dni', 'dni')}</Typography>
                      </TableCell>
                      <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                      <TableCell align="right">
                        {r.status === 'PENDING' && (
                          <Stack direction="row" gap={1} justifyContent="flex-end">
                            <Button size="small" variant="text" color="error" onClick={() => reject(r)}>Odrzuć</Button>
                            <Button size="small" variant="outlined" onClick={() => review(r.id, 'APPROVED')}>Zatwierdź</Button>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* Dodawanie nieobecności pracownikowi (od razu zatwierdzona) */}
      <Dialog open={addOpen} onClose={() => !addSaving && setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Dodaj nieobecność</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {addError && ![ERR_STAFF, ERR_DATES].includes(addError) && (
              <Alert severity="error" onClose={() => setAddError('')}>{addError}</Alert>
            )}
            <TextField select fullWidth label="Pracownik" value={addForm.staffId}
              error={addError === ERR_STAFF} helperText={addError === ERR_STAFF ? ERR_STAFF : undefined}
              onChange={(e) => setAddForm({ ...addForm, staffId: e.target.value })}>
              {staffList.map((s) => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Rodzaj nieobecności" value={addForm.type}
              onChange={(e) => setAddForm({ ...addForm, type: e.target.value })}>
              {Object.keys(TYPE_LABEL).map((v) => <MenuItem key={v} value={v}>{TYPE_OPTION_LABEL[v]}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <TextField type="date" fullWidth label="Od" InputLabelProps={{ shrink: true }}
                error={addError === ERR_DATES && !addForm.startDate}
                value={addForm.startDate} onChange={(e) => setAddForm({ ...addForm, startDate: e.target.value })} />
              <TextField type="date" fullWidth label="Do" InputLabelProps={{ shrink: true }}
                error={addError === ERR_DATES && !addForm.endDate}
                helperText={addError === ERR_DATES ? ERR_DATES : undefined}
                value={addForm.endDate} onChange={(e) => setAddForm({ ...addForm, endDate: e.target.value })} />
            </Box>
            <TextField fullWidth label="Notatka (opcjonalnie)" multiline rows={2}
              value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} />
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              Nieobecność dodana przez właściciela lub managera jest od razu zatwierdzona i widoczna
              w kalendarzu pracy oraz w panelu pracownika.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setAddOpen(false)} disabled={addSaving}>Anuluj</Button>
          <Button variant="contained" onClick={submitAdd} disabled={addSaving}
            startIcon={addSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {addSaving ? 'Zapisywanie…' : 'Dodaj nieobecność'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AbsencesAdminPage;
